import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FAQSection from "@/components/feature/FAQSection";
import AnimatedSection from "@/components/AnimatedSection";

const webData = {
  hero: {
    badge: "Web Development",
    headline: "Custom Web Applications Built for Your Business",
    subtext: "We build modern web applications using Laravel, PHP, Vue.js and React designed around your business processes and user needs.",
    primaryCTA: "Start Your Project",
    secondaryCTA: "Talk to a Laravel Expert",
  },
  services: [
    { title: "Custom Web Applications", desc: "Tailored web applications built for your unique business requirements and workflows.", icon: "ri-window-line" },
    { title: "Business Portals", desc: "Internal and external portals for employees, partners and customers.", icon: "ri-building-4-line" },
    { title: "Admin Panels", desc: "Powerful admin dashboards with data visualization and management tools.", icon: "ri-dashboard-line" },
    { title: "CRM Development", desc: "Customer relationship management systems tailored to your sales process.", icon: "ri-customer-service-line" },
    { title: "ERP Development", desc: "Enterprise resource planning systems for operations and finance.", icon: "ri-bar-chart-box-line" },
    { title: "E-commerce", desc: "Custom online stores with payment integration, inventory and analytics.", icon: "ri-shopping-bag-2-line" },
    { title: "Enterprise Applications", desc: "Large-scale applications with security, compliance and integration at the core.", icon: "ri-building-line" },
    { title: "Backend Systems", desc: "Robust backend APIs, services and data processing pipelines.", icon: "ri-server-line" },
  ],
  process: [
    { number: "01", title: "Discovery", desc: "Understand your business requirements and user needs." },
    { number: "02", title: "Planning", desc: "Define architecture, technology stack and project roadmap." },
    { number: "03", title: "Design", desc: "Create UI/UX wireframes and interactive prototypes." },
    { number: "04", title: "Development", desc: "Build with agile sprints and continuous integration." },
    { number: "05", title: "Testing", desc: "QA, security audits and performance testing." },
    { number: "06", title: "Deployment", desc: "Production deployment with monitoring and CI/CD." },
    { number: "07", title: "Support", desc: "Ongoing maintenance and feature enhancements." },
  ],
  techStack: [
    { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg", featured: true },
    { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg" },
    { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg" },
    { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
    { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
    { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
    { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
    { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
    { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
    { name: "Tailwind CSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg" },
  ],
  faq: [
    { q: "What technologies do you use for web development?", a: "We primarily use Laravel and PHP for backend, Vue.js and React for frontend, MySQL and PostgreSQL for databases, and AWS or DigitalOcean for cloud hosting." },
    { q: "Can you build a custom CRM for our business?", a: "Yes. We build custom CRMs with lead management, pipeline tracking, email integration, reporting and role-based access tailored to your sales process." },
    { q: "Do you provide responsive web design?", a: "Yes. All our web applications are fully responsive and optimized for desktop, tablet and mobile devices using Tailwind CSS." },
    { q: "How do you handle web application security?", a: "We follow OWASP guidelines, implement CSRF protection, SQL injection prevention, XSS filtering, input validation, rate limiting and regular security audits." },
  ],
};

export default function WebDevelopmentPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/services" },
          { label: "Web Development" },
        ]}
      />

      <PageHero
        badge={webData.hero.badge}
        headline={webData.hero.headline}
        subtext={webData.hero.subtext}
        primaryCTA={webData.hero.primaryCTA}
        secondaryCTA={webData.hero.secondaryCTA}
      />

      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Web Development Services
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {webData.services.map((service, i) => (
              <AnimatedSection key={service.title} delay={i * 100}>
                <div className="group p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${service.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">
                    {service.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {service.desc}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Our Technology Stack
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {webData.techStack.map((tech, i) => (
              <AnimatedSection key={tech.name} delay={i * 60}>
                <div className={`flex flex-col items-center gap-3 p-5 md:p-6 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300 card-lift ${tech.featured ? "ring-2 ring-accent-500/20" : ""}`}>
                  <img src={tech.logo} alt={tech.name} className="w-10 h-10 object-contain" />
                  <span className={`text-sm font-semibold ${tech.featured ? "text-accent-600" : "text-foreground-800"}`}>
                    {tech.name}
                  </span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Development Process
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute top-[52px] left-[7%] right-[7%] h-px bg-gradient-to-r from-accent-500/20 via-accent-500/40 to-accent-500/20" />
              <div className="grid grid-cols-7 gap-4">
                {webData.process.map((step, i) => (
                  <AnimatedSection key={step.number} delay={i * 120}>
                    <div className="text-center group">
                      <div className="relative inline-flex items-center justify-center w-[52px] h-[52px] rounded-full bg-background-100 border-2 border-accent-500/30 text-accent-600 font-extrabold text-sm font-heading mb-5 mx-auto group-hover:bg-accent-500 group-hover:text-background-50 group-hover:border-accent-500 transition-all duration-300 z-10">
                        {step.number}
                      </div>
                      <h3 className="text-sm font-bold text-foreground-950 mb-2 font-heading">{step.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed max-w-[140px] mx-auto">{step.desc}</p>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:hidden">
            <div className="relative max-w-md mx-auto">
              <div className="absolute left-[25px] top-0 bottom-0 w-px bg-gradient-to-b from-accent-500/30 via-accent-500/50 to-accent-500/30" />
              <div className="flex flex-col gap-8">
                {webData.process.map((step, i) => (
                  <AnimatedSection key={step.number} delay={i * 100}>
                    <div className="flex items-start gap-5 group">
                      <div className="relative z-10 flex items-center justify-center w-[52px] h-[52px] rounded-full bg-background-100 border-2 border-accent-500/30 text-accent-600 font-extrabold text-sm font-heading shrink-0 group-hover:bg-accent-500 group-hover:text-background-50 group-hover:border-accent-500 transition-all duration-300">
                        {step.number}
                      </div>
                      <div className="pt-2.5">
                        <h3 className="text-base font-bold text-foreground-950 mb-1.5 font-heading">{step.title}</h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQSection heading="Web Development FAQ" items={webData.faq} />

      <PageCTA
        heading="Have a project in mind?"
        text="Let's build something that scales."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />
    </PageLayout>
  );
}